import styles from '../ProductPageLayout/ProductPageLayout.module.css'
import { FC, useState, useMemo, useRef } from 'react'
import { CDN_BASE as CDN_ROOT } from '~/lib/cdn'
import { ConfiguratorPageShell } from '~/components/ConfiguratorPageShell/ConfiguratorPageShell'
import { useLocalizedRouter } from '~/components/LocalizedLink/LocalizedLink'
import { FormattedMessage, useIntl } from 'react-intl'
import { Slider } from '~/components/Slider/Slider'
import {
  ButtonOptionsType,
  ButtonSelect,
} from '~/components/ButtonSelect/ButtonSelect'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { Modal } from '~/components/Modal/Modal'
import { ShareConfigModal } from '~/components/ShareConfigModal/ShareConfigModal'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  TableViewer,
  TableViewerRef,
} from '~/components/TableViewer/TableViewer'
import SelectColor from '~/components/SelectColor/SelectColor'
import { useCart } from '~/context/cartContext'
import {
  TABLE_WIDTH_RANGE,
  TABLE_WIDTH_STEP,
  TABLE_DEPTH_OPTIONS,
  TABLE_DEFAULTS,
  TABLE_HEIGHT,
} from '~/components/ProductPage/productTypes/table'
import { getColorItemByName } from '~/utils/colorDictionary'
import type { TableMaterial } from '~/components/ProductPage/productTypes/table'
import { useMediaQuery, IconButton } from '@mui/material'
import { Banner } from '~/components/Banner/Banner'
import { BannerTable } from '~/components/Banner/BannerTable'
import Grid2 from '~/components/Grid/grid2'
import { RequestOfferModal } from '~/components/RequestOfferModal/RequestOfferModal'

type TableDimensionsComponent = {
  type: 'dimensions'
  width: number
  setWidth: (value: number) => void
  depth: number
  setDepth: (value: number) => void
  predefinedValue?: {
    width: number
    height: number
    depth: number
    plintHeight: number
  }
  height: number
  plintHeight: number
}

type TableMaterialComponent = {
  type: 'material'
  material: TableMaterial
  setMaterial: (value: TableMaterial) => void
}

type TableTextureComponent = {
  type: 'texture'
  textures: string[]
  selectedTexture: string
  setSelectedTexture: (value: string) => void
}

type TablePriceComponent = {
  type: 'price'
  price: number
  predefinedValue?: number
}

export type ProductComponent =
  | TableDimensionsComponent
  | TableMaterialComponent
  | TableTextureComponent
  | TablePriceComponent

export type PredefinedValue = {
  dimensions?: { width: number; depth: number }
  price?: number
  screenshot?: string
}

interface TableProductPageProps {
  components: () => ProductComponent[]
  name: string
}

const CDN_BASE = `${CDN_ROOT}/table/renders`

const depthOptions: ButtonOptionsType[] = TABLE_DEPTH_OPTIONS.map((d) => ({
  value: String(d),
  label: (
    <span>
      {d} <FormattedMessage id="homepage.configurator.dimensions.cm" />
    </span>
  ),
}))

const heightOptions: ButtonOptionsType[] = [
  {
    value: String(TABLE_HEIGHT),
    label: (
      <span>
        {TABLE_HEIGHT}{' '}
        <FormattedMessage id="homepage.configurator.dimensions.cm" />
      </span>
    ),
  },
]

const materialOptions: ButtonOptionsType<TableMaterial>[] = [
  {
    value: 'chipboard',
    label: (
      <FormattedMessage id="homepage.configurator.table.material.chipboard" />
    ),
  },
  {
    value: 'plywood',
    label: (
      <FormattedMessage id="homepage.configurator.table.material.plywood" />
    ),
  },
]

type TableTab = 'dimensions' | 'material'

const tabOptions: ButtonOptionsType<TableTab>[] = [
  {
    value: 'dimensions',
    label: <FormattedMessage id="homepage.configurator.dimensions.title" />,
  },
  {
    value: 'material',
    label: (
      <FormattedMessage id="homepage.configurator.individualColumns.title" />
    ),
  },
]

export const ProductPage: FC<TableProductPageProps> = ({
  components,
  name,
}) => {
  const router = useLocalizedRouter()
  const intl = useIntl()
  const { addItem } = useCart()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [activeTab, setActiveTab] = useState<TableTab>('dimensions')
  const [isCartModalOpen, setIsCartModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const [offerConfigUrl, setOfferConfigUrl] = useState('')
  const tableViewerRef = useRef<TableViewerRef>(null)

  const currentComponents = components()

  const dimensionsComponent = currentComponents.find(
    (c) => c.type === 'dimensions'
  ) as TableDimensionsComponent | undefined

  const materialComponent = currentComponents.find(
    (c) => c.type === 'material'
  ) as TableMaterialComponent | undefined

  const textureComponent = currentComponents.find(
    (c) => c.type === 'texture'
  ) as TableTextureComponent | undefined

  const priceComponent = currentComponents.find((c) => c.type === 'price') as
    | TablePriceComponent
    | undefined

  const width = dimensionsComponent?.width ?? TABLE_DEFAULTS.width
  const depth = dimensionsComponent?.depth ?? TABLE_DEFAULTS.depth
  const originalPrice = priceComponent?.price ?? 0
  const selectedTexture = textureComponent?.selectedTexture

  const round10 = (n: number): number => Math.round(n / 10) * 10
  const price = round10(originalPrice * 0.7)

  const renderSlug = useMemo(() => {
    if (!selectedTexture) return 'natural-accacia'
    return getColorItemByName(selectedTexture)?.fileURL ?? 'natural-accacia'
  }, [selectedTexture])

  const renderImages = useMemo(
    () => [1, 2, 3].map((i) => `${CDN_BASE}/large/${i}/${renderSlug}.webp`),
    [renderSlug]
  )

  const renderThumbnail = `${CDN_BASE}/thumbnails/1/${renderSlug}.jpg`

  const material = materialComponent?.material ?? 'plywood'

  const buildShareUrl = () => {
    if (typeof window === 'undefined') return ''
    const pathSegments = window.location.pathname.split('/')
    const locale = pathSegments[1] || 'ro'
    const params = new URLSearchParams({
      width: String(width),
      depth: String(depth),
      material,
      ...(selectedTexture ? { texture: selectedTexture } : {}),
    })
    return `${window.location.origin}/${locale}/configurator/table?${params}`
  }

  const handleAddToCart = () => {
    const screenshot = tableViewerRef.current?.captureScreenshot() ?? undefined
    const cartComponents = currentComponents.map((c) =>
      c.type === 'price' ? { ...c, price } : c
    )
    addItem('table', cartComponents, { screenshot })
    setIsCartModalOpen(true)
  }

  const materialTooltip = intl.formatMessage({
    id: 'homepage.configurator.table.material.tooltip',
  })

  const renderDimensionsSection = () => (
    <div className={styles.dimensionsGrid}>
      <label className={styles.dimensionLabel}>
        <p className={styles.dimensionTitle}>
          <FormattedMessage id="homepage.configurator.dimensions.width" />
        </p>
        <div className={styles.dimensionControl}>
          <Slider
            min={TABLE_WIDTH_RANGE[0]}
            max={TABLE_WIDTH_RANGE[1]}
            step={TABLE_WIDTH_STEP}
            value={width}
            onChange={(val) => dimensionsComponent?.setWidth(val)}
          />
        </div>
      </label>

      <label className={styles.dimensionLabel}>
        <p className={styles.dimensionTitle}>
          <FormattedMessage id="homepage.configurator.dimensions.depth" />
        </p>
        <ButtonSelect
          options={depthOptions}
          defaultSelected={String(depth)}
          onChange={(val) => dimensionsComponent?.setDepth(Number(val))}
        />
      </label>

      <label className={styles.dimensionLabel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <p className={styles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.dimensions.height" />
            <span
              className={styles.dimensionsTooltipContainer}
              style={{ marginTop: 0 }}
              title="Înălțimea mesei este fixă — 75 cm"
            >
              <HelpOutlineIcon color="action" sx={{ fontSize: 18 }} />
            </span>
          </p>
        </div>
        <ButtonSelect
          options={heightOptions}
          defaultSelected={String(TABLE_HEIGHT)}
        />
      </label>
    </div>
  )

  const renderMaterialSection = () => (
    <>
      {materialComponent && (
        <label className={styles.dimensionLabel}>
          <p className={styles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.table.material.title" />
          </p>
          <ButtonSelect<TableMaterial>
            options={materialOptions}
            defaultSelected={materialComponent.material}
            onChange={(val) => materialComponent.setMaterial(val)}
          />
        </label>
      )}

      {textureComponent && (
        <label className={styles.colorsLabel} style={{ marginTop: 10 }}>
          <p className={styles.dimensionTitle}>
            <FormattedMessage id="homepage.configurator.table.texture.title" />
          </p>
          <SelectColor
            colors={textureComponent.textures}
            onChange={(value) => textureComponent.setSelectedTexture(value)}
            defaultSelected={textureComponent.selectedTexture}
            size="medium"
          />
        </label>
      )}
    </>
  )

  // h2, not h1: the prerender-safe canonical h1 for this route is rendered by
  // <SeoContentSection> (main.tsx), outside the lazy route boundary.
  const heading = (
    <h2 className={styles.title}>
      <FormattedMessage id={name} />
    </h2>
  )

  const priceSection = (
    <div className={styles.priceCont}>
      <div className={styles.priceRow}>
        <div
          className={styles.priceBox}
          style={
            isMobile
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                }
              : undefined
          }
        >
          <p className={styles.newPrice}>
            {price}&nbsp;
            <FormattedMessage id="homepage.configurator.price.currencyLei" />
          </p>
          <p
            className={styles.crossedPrice}
            style={
              !isMobile
                ? { margin: '7px 0 0 10px', fontSize: 13 }
                : { margin: 0 }
            }
          >
            {originalPrice}&nbsp;
            <FormattedMessage id="homepage.configurator.price.currencyLei" />
          </p>
        </div>

        <div className={styles.addToCartButtonContainer}>
          <div className={styles.cartButtonWrapper}>
            <CustomButton
              icon={<LocalPhoneIcon />}
              size="medium"
              variant="primary"
              onClick={() => {
                setOfferConfigUrl(buildShareUrl())
                setIsOfferModalOpen(true)
              }}
            >
              <FormattedMessage id="homepage.button.requestOffer" />
            </CustomButton>
          </div>
          <div className={styles.cartButtonWrapper}>
            <IconButton
              onClick={handleAddToCart}
              className={styles.cartIconButton}
            >
              <ShoppingCartIcon />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )

  const controls = isMobile ? (
    <>
      <div className={styles.mobileNavChips}>
        <ButtonSelect<TableTab>
          options={tabOptions}
          defaultSelected={activeTab}
          size="medium"
          className={styles.mobileNavChipsRow}
          onChange={(val) => setActiveTab(val)}
        />
      </div>

      <div
        className={styles.mobileCorePanel}
        style={{ flexDirection: 'column', alignItems: 'start' }}
      >
        {activeTab === 'dimensions' && renderDimensionsSection()}
        {activeTab === 'material' && renderMaterialSection()}
      </div>
    </>
  ) : (
    <>
      <h3 className={styles.dimensionsHeaderTitle}>
        <FormattedMessage id="homepage.configurator.dimensions.title" />
      </h3>
      {renderDimensionsSection()}

      <h3
        className={styles.dimensionsHeaderTitle}
        title={materialTooltip}
        style={{ marginTop: 20 }}
      >
        <FormattedMessage id="homepage.configurator.individualColumns.title" />
      </h3>
      {renderMaterialSection()}
    </>
  )

  const belowFoldExtra = (
    <>
      <div style={{ marginTop: 30 }}>
        <Banner
          href="/cdn/table/landing.webp"
          mobileHref="/cdn/table/landing-mobile.webp"
          content={BannerTable}
        />
      </div>

      <section>
        <Grid2
          hasPageMargings={true}
          images={[
            { src: '/cdn/table/1.webp' },
            { src: '/cdn/table/2.webp' },
            { src: '/cdn/table/3.webp' },
            { src: '/cdn/table/4.webp' },
            { src: '/cdn/table/5.webp' },
          ]}
          href="/"
        />
      </section>
    </>
  )

  const modals = (
    <>
      <Modal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)}>
        <h3>
          <FormattedMessage id="modal.addedToCart.title" />
        </h3>
        <div className={styles.buttonRow}>
          <CustomButton onClick={() => router.push('/cart')}>
            <FormattedMessage id="homepage.button.openCart" />
          </CustomButton>
          <CustomButton variant="grey" onClick={() => router.push('/')}>
            <FormattedMessage id="homepage.button.backToPurchase" />
          </CustomButton>
        </div>
      </Modal>

      <ShareConfigModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        url={shareUrl}
      />
      <RequestOfferModal
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        configUrl={offerConfigUrl}
        productSummary={`Masă ${width}×${depth}×${TABLE_HEIGHT}cm — ${price} lei`}
      />
    </>
  )

  return (
    <ConfiguratorPageShell
      viewer={
        <TableViewer
          ref={tableViewerRef}
          width={width}
          depth={depth}
          selectedTexture={selectedTexture}
          onShare={() => {
            setShareUrl(buildShareUrl())
            setIsShareModalOpen(true)
          }}
          onExport={() => tableViewerRef.current?.exportModel()}
        />
      }
      renderThumbnails={[renderThumbnail]}
      heading={heading}
      priceSection={priceSection}
      controls={controls}
      renderImages={renderImages}
      showOrderProgress
      orderProgressVariant="table"
      showInfoBar
      belowFoldExtra={belowFoldExtra}
      modals={modals}
    />
  )
}
